/**
 * Usage Dashboard Page
 *
 * Displays the user's token usage: daily progress ring, weekly bar chart,
 * monthly summary, and upgrade CTA when approaching limits.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  AlertTriangle,
  TrendingUp,
  Zap,
  BarChart3,
  Sparkles,
  Infinity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { billingApi } from '@/services/billingApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UsageSummary {
  totalTokens?: number;
  totalUsageQuantity?: number;
  dailyUsed?: number;
  dailyLimit?: number;
  monthlyUsed?: number;
  monthlyLimit?: number;
  dailyHistory?: Array<{ date: string; tokens: number }>;
  topModels?: Array<{ model: string; tokens: number }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatNumber = (n: number): string => n.toLocaleString();

const isUnlimited = (limit: number | undefined | null): boolean =>
  limit === undefined || limit === null || limit === -1;

/** SVG circular progress ring. */
const CircularProgress: React.FC<{ percentage: number; size?: number; strokeWidth?: number }> = ({
  percentage,
  size = 160,
  strokeWidth = 12,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(percentage, 100));
  const offset = circumference - (clamped / 100) * circumference;

  const color =
    percentage >= 90
      ? 'stroke-red-500'
      : percentage >= 70
        ? 'stroke-amber-500'
        : 'stroke-purple-500';

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="transparent"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted/30"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="transparent"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className={`${color} transition-all duration-700`}
      />
    </svg>
  );
};

/** Simple bar chart using divs. */
const BarChart: React.FC<{ data: Array<{ date: string; tokens: number }> }> = ({ data }) => {
  const max = Math.max(...data.map((d) => d.tokens), 1);

  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((entry) => {
        const heightPct = (entry.tokens / max) * 100;
        const dayLabel = new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short' });
        return (
          <div key={entry.date} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[10px] text-muted-foreground">{formatNumber(entry.tokens)}</span>
            <div className="w-full rounded-t-md bg-muted/30 relative" style={{ height: '100%' }}>
              <div
                className="absolute bottom-0 left-0 right-0 rounded-t-md bg-gradient-to-t from-purple-600 to-fuchsia-500 transition-all duration-500"
                style={{ height: `${heightPct}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{dayLabel}</span>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const UsageDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError(null);
      const raw = await billingApi.getUserUsageSummary(user.id);
      // Normalise various backend response shapes
      const summary: UsageSummary = {
        totalTokens: Number(raw?.totalTokens ?? raw?.totalUsageQuantity ?? 0),
        dailyUsed: Number(raw?.dailyUsed ?? raw?.totalUsageQuantity ?? 0),
        dailyLimit: raw?.dailyLimit !== undefined ? Number(raw.dailyLimit) : -1,
        monthlyUsed: Number(raw?.monthlyUsed ?? 0),
        monthlyLimit: raw?.monthlyLimit !== undefined ? Number(raw.monthlyLimit) : -1,
        dailyHistory: Array.isArray(raw?.dailyHistory) ? raw.dailyHistory as UsageSummary['dailyHistory'] : undefined,
        topModels: Array.isArray(raw?.topModels) ? raw.topModels as UsageSummary['topModels'] : undefined,
      };
      setUsage(summary);
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to load usage data';
      if (msg.includes('Authentication required')) {
        navigate('/login?next=/billing/usage', { replace: true });
        return;
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, navigate]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Derived values
  const dailyUsed = usage?.dailyUsed ?? 0;
  const dailyLimit = usage?.dailyLimit ?? -1;
  const unlimited = isUnlimited(dailyLimit);
  const dailyPct = unlimited ? 0 : Math.round((dailyUsed / dailyLimit) * 100);
  const approachingLimit = !unlimited && dailyPct >= 80;

  // Placeholder weekly data if not provided by API
  const weeklyData: Array<{ date: string; tokens: number }> = usage?.dailyHistory ??
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { date: d.toISOString().split('T')[0], tokens: i === 6 ? dailyUsed : 0 };
    });

  // --- Loading / error states ---

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex items-center justify-center py-32 px-4">
          <BrandedLoading message="Loading usage…" iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center gap-4 py-32 px-4 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchUsage}>
            Retry
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 space-y-8">
          {/* Back */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Button variant="ghost" size="sm" onClick={() => navigate('/billing')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Billing
            </Button>
          </motion.div>

          {/* Heading */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-1">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Usage Dashboard</h1>
            <p className="text-muted-foreground">Track your AI coaching token usage</p>
          </motion.div>

          {/* Upgrade CTA banner (if approaching limit) */}
          {approachingLimit && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-purple-500/10 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-amber-600 shrink-0" />
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  You have used {dailyPct}% of your daily token limit. Upgrade to Pro for more capacity.
                </p>
              </div>
              <Button
                size="sm"
                className="shrink-0 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white"
                onClick={() => navigate('/billing/plans')}
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Upgrade
              </Button>
            </motion.div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Daily progress ring */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Today's Usage
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-3">
                  <div className="relative">
                    <CircularProgress percentage={unlimited ? 0 : dailyPct} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-foreground">{formatNumber(dailyUsed)}</span>
                      <span className="text-xs text-muted-foreground">
                        / {unlimited ? (
                          <span className="inline-flex items-center gap-0.5"><Infinity className="h-3 w-3" /> Unlimited</span>
                        ) : formatNumber(dailyLimit)}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">tokens used today</span>
                </CardContent>
              </Card>
            </motion.div>

            {/* Weekly bar chart */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="md:col-span-2">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Last 7 Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart data={weeklyData} />
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Monthly summary + top models */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monthly summary */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Monthly Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Tokens used this month</span>
                    <span className="text-lg font-bold text-foreground">{formatNumber(usage?.monthlyUsed ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Monthly limit</span>
                    <span className="text-sm font-medium text-foreground">
                      {isUnlimited(usage?.monthlyLimit) ? (
                        <span className="inline-flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Unlimited</span>
                      ) : formatNumber(usage?.monthlyLimit ?? 0)}
                    </span>
                  </div>
                  {!isUnlimited(usage?.monthlyLimit) && (usage?.monthlyLimit ?? 0) > 0 && (
                    <div className="w-full h-2 bg-muted/40 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, ((usage?.monthlyUsed ?? 0) / (usage?.monthlyLimit ?? 1)) * 100)}%` }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Top models */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Top Models Used
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {usage?.topModels && usage.topModels.length > 0 ? (
                    <ul className="space-y-2">
                      {usage.topModels.map((m) => {
                        const total = usage.topModels!.reduce((s, x) => s + x.tokens, 0) || 1;
                        const pct = Math.round((m.tokens / total) * 100);
                        return (
                          <li key={m.model} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-foreground font-medium truncate">{m.model}</span>
                              <span className="text-xs text-muted-foreground">{formatNumber(m.tokens)} ({pct}%)</span>
                            </div>
                            <div className="w-full h-1.5 bg-muted/40 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Model usage data will appear here as you use coaching.
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UsageDashboardPage;
